import React, { useState, useContext } from "react"
import {
  HiX,
  HiOutlineChip,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineBadgeCheck,
  HiOutlineSearch,
} from "react-icons/hi"
import AuthContext from "store/context/PublicContext"
import { post_middleware_series_create, post_middleware_osrm_search } from "services/middleware_services"

interface CreateSerieModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialData?: any
}

const ESTADO_OPTIONS = [
  { value: "0 - libre", label: "0 - Libre" },
  { value: "1 - vendido", label: "1 - Vendido" },
  { value: "4 - reservado", label: "4 - Reservado" },
  { value: "6 - eliminado", label: "6 - Eliminado" },
  { value: "7 - bloqueado", label: "7 - Bloqueado" },
]

const INPUT_CLS =
  "w-full bg-[#F2F2F7] border border-transparent rounded-xl px-4 py-3 text-[13px] font-medium text-gray-800 placeholder-gray-400 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all"

const LABEL_CLS = "text-[11px] font-black text-gray-500 uppercase tracking-widest"
const OPT_LABEL_CLS = "text-[10px] font-black text-gray-400 uppercase tracking-widest"
const OPT_INPUT_CLS =
  "w-full bg-[#F2F2F7] border border-transparent rounded-xl px-3 py-2.5 text-[12px] font-medium text-gray-800 placeholder-gray-400 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all"

const CreateSerieModal: React.FC<CreateSerieModalProps> = ({ isOpen, onClose, onSuccess, initialData }) => {
  const {
    user: { token, email, username },
  } = useContext(AuthContext)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  const [serieStatus, setSerieStatus] = useState<"idle" | "checking" | "valid" | "invalid">("idle")
  const [skuStatus, setSkuStatus] = useState<"idle" | "checking" | "valid" | "invalid">("idle")
  const [skuProductName, setSkuProductName] = useState("")
  const [almacenStatus, setAlmacenStatus] = useState<"idle" | "checking" | "valid" | "invalid">("idle")
  const [almacenName, setAlmacenName] = useState("")

  const [form, setForm] = useState({
    serieSap: "",
    sku: "",
    almacen: "",
    sociedad: "",
    centro: "",
    nomBodega: "",
    tipoSku: "",
    claseSku: "",
    almRecep: "",
    subalmacen: "",
    estadoOmnix: "0 - libre",
  })

  React.useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setForm({
          serieSap: "", // Always leave serieSap empty so user inputs a new one
          sku: initialData.sku || "",
          almacen: initialData.almacen || "",
          sociedad: initialData.sociedad || "",
          centro: initialData.centro || "",
          nomBodega: initialData.nomBodega || "",
          tipoSku: initialData.tipoSku || "",
          claseSku: initialData.claseSku || "",
          almRecep: initialData.almRecep || "",
          subalmacen: initialData.subalmacen || "",
          estadoOmnix: initialData.estadoOmnix || "0 - libre",
        })
        setSerieStatus("idle")
        setSkuStatus(initialData.sku ? "valid" : "idle")
        setSkuProductName(initialData.sku ? "Cargado de clonación" : "")
        setAlmacenStatus(initialData.almacen ? "valid" : "idle")
        setAlmacenName(initialData.almacen ? "Cargado de clonación" : "")
      } else {
        setForm({
          serieSap: "",
          sku: "",
          almacen: "",
          sociedad: "",
          centro: "",
          nomBodega: "",
          tipoSku: "",
          claseSku: "",
          almRecep: "",
          subalmacen: "",
          estadoOmnix: "0 - libre",
        })
        setSerieStatus("idle")
        setSkuStatus("idle")
        setSkuProductName("")
        setAlmacenStatus("idle")
        setAlmacenName("")
      }
      setError(null)
      setSuccess(false)
    }
  }, [isOpen, initialData])

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setError(null)
    if (field === "serieSap") setSerieStatus("idle")
    if (field === "sku") {
      setSkuStatus("idle")
      setSkuProductName("")
    }
    if (field === "almacen") {
      setAlmacenStatus("idle")
      setAlmacenName("")
    }
  }

  const validateSerieSap = async () => {
    if (!form.serieSap.trim()) return
    setSerieStatus("checking")
    try {
      const res = await post_middleware_osrm_search(
        "series",
        { from: 0, size: 1, filters: { serieSap: form.serieSap.trim() } },
        token,
      )
      const records = res?.data?.message?.records
      if (records && records.length > 0) {
        setSerieStatus("invalid") // already exists
      } else {
        setSerieStatus("valid") // available
      }
    } catch {
      setSerieStatus("invalid")
    }
  }

  const validateAlmacen = async () => {
    if (!form.almacen.trim()) { setAlmacenStatus("idle"); return }
    setAlmacenStatus("checking")
    try {
      const res = await post_middleware_osrm_search(
        "sources",
        { from: 0, size: 1, filters: { "custom.codVTASA": form.almacen.trim() } },
        token,
      )
      const records = res?.data?.message?.records
      if (records && records.length > 0) {
        setAlmacenStatus("valid")
        setAlmacenName(records[0]?.name || records[0]?.criteria0 || "")
      } else {
        setAlmacenStatus("invalid")
        setAlmacenName("")
      }
    } catch {
      setAlmacenStatus("invalid")
      setAlmacenName("")
    }
  }

  const validateSku = async () => {
    if (!form.sku.trim()) return
    setSkuStatus("checking")
    try {
      const res = await post_middleware_osrm_search(
        "items",
        { from: 0, size: 1, filters: { sku: form.sku.trim() } },
        token,
      )
      const records = res?.data?.message?.records
      if (records && records.length > 0) {
        setSkuStatus("valid")
        setSkuProductName(records[0]?.name || records[0]?.sku || "")
      } else {
        setSkuStatus("invalid")
        setSkuProductName("")
      }
    } catch {
      setSkuStatus("invalid")
      setSkuProductName("")
    }
  }

  const handleSubmit = async () => {
    if (!form.serieSap.trim()) { setError("El campo Serie SAP es obligatorio."); return }
    if (serieStatus !== "valid") { setError("Debes validar que la Serie SAP esté disponible."); return }
    if (!form.sku.trim()) { setError("El campo SKU es obligatorio."); return }
    if (skuStatus !== "valid") { setError("Debes validar el SKU antes de crear la serie."); return }
    if (form.almacen.trim() && almacenStatus !== "valid") { setError("Debes validar el Almacén."); return }

    setLoading(true)
    setError(null)
    try {
      const body: any = {
        serieSap: form.serieSap.trim(),
        sku: form.sku.trim(),
        estadoOmnix: form.estadoOmnix,
      }
      if (form.almacen.trim()) body.almacen = form.almacen.trim()
      if (form.sociedad.trim()) body.sociedad = form.sociedad.trim()
      if (form.centro.trim()) body.centro = form.centro.trim()
      if (form.nomBodega.trim()) body.nomBodega = form.nomBodega.trim()
      if (form.tipoSku.trim()) body.tipoSku = form.tipoSku.trim()
      if (form.claseSku.trim()) body.claseSku = form.claseSku.trim()
      if (form.almRecep.trim()) body.almRecep = form.almRecep.trim()
      if (form.subalmacen.trim()) body.subalmacen = form.subalmacen.trim()

      const userMail = email || username || "system@omnix.com"
      await post_middleware_series_create(body, token, userMail, "frontend-xmonitor")

      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
        setSkuStatus("idle")
        setSkuProductName("")
        setSerieStatus("idle")
        setAlmacenStatus("idle")
        setAlmacenName("")
        setForm({ serieSap: "", sku: "", almacen: "", sociedad: "", centro: "", nomBodega: "", tipoSku: "", claseSku: "", almRecep: "", subalmacen: "", estadoOmnix: "0 - libre" })
        onSuccess()
        onClose()
      }, 1200)
    } catch (err: any) {
      const msg =
        err?.response?.data?.message?.message ||
        err?.response?.data?.message ||
        "Error al crear la serie. Verifica los datos e intenta nuevamente."
      setError(typeof msg === "string" ? msg : JSON.stringify(msg))
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 sm:p-0">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet — mobile: floating bottom sheet with margins, desktop: centered card */}
      <div
        className="relative z-10 w-full sm:max-w-md flex flex-col bg-white
          rounded-3xl sm:rounded-2xl shadow-2xl
          max-h-[90dvh] sm:max-h-[90vh]
          animate-slide-up overflow-hidden mb-2 sm:mb-0"
      >
        {/* Drag handle (mobile only) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header — sticky */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 pt-4 sm:pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <HiOutlineChip className="text-indigo-500 text-lg" />
            </div>
            <div>
              <h2 className="text-[15px] font-black text-gray-900 tracking-tight">Nueva Serie</h2>
              <p className="text-[11px] text-gray-400 font-medium">Registro individual de serie SAP</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            <HiX className="text-sm" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 flex flex-col gap-4">

          {/* Serie SAP (required) */}
          <div className="flex flex-col gap-1.5">
            <label className={LABEL_CLS}>
              Serie SAP <span className="text-rose-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.serieSap}
                onChange={(e) => handleChange("serieSap", e.target.value)}
                placeholder="Ej: 89511710101052380976"
                className={`${INPUT_CLS} flex-1 min-w-0`}
                onKeyDown={(e) => e.key === "Enter" && validateSerieSap()}
              />
              <button
                type="button"
                onClick={validateSerieSap}
                disabled={!form.serieSap.trim() || serieStatus === "checking"}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl
                  bg-indigo-500 hover:bg-indigo-600 active:scale-[0.97]
                  text-white text-[12px] font-black uppercase tracking-widest
                  shadow-sm shadow-indigo-200 transition-all
                  disabled:opacity-40 disabled:shadow-none whitespace-nowrap"
              >
                {serieStatus === "checking" ? (
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <HiOutlineSearch className="text-sm" />
                )}
                Validar
              </button>
            </div>
            {/* Serie validation feedback */}
            {serieStatus === "valid" && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
                <HiOutlineBadgeCheck className="text-emerald-500 text-base flex-shrink-0" />
                <span className="text-[11px] font-semibold text-emerald-700 truncate">
                  Serie disponible para creación
                </span>
              </div>
            )}
            {serieStatus === "invalid" && (
              <div className="flex items-center gap-2 px-3 py-2 bg-rose-50 border border-rose-100 rounded-xl">
                <HiOutlineExclamationCircle className="text-rose-500 text-base flex-shrink-0" />
                <span className="text-[11px] font-semibold text-rose-700">La serie ya existe en el sistema</span>
              </div>
            )}
          </div>

          {/* SKU + Validar */}
          <div className="flex flex-col gap-1.5">
            <label className={LABEL_CLS}>
              SKU <span className="text-rose-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.sku}
                onChange={(e) => handleChange("sku", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && validateSku()}
                placeholder="Ej: SUPERCHIPEPLUS2"
                className={`${INPUT_CLS} flex-1 min-w-0`}
              />
              <button
                type="button"
                onClick={validateSku}
                disabled={!form.sku.trim() || skuStatus === "checking"}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl
                  bg-indigo-500 hover:bg-indigo-600 active:scale-[0.97]
                  text-white text-[12px] font-black uppercase tracking-widest
                  shadow-sm shadow-indigo-200 transition-all
                  disabled:opacity-40 disabled:shadow-none whitespace-nowrap"
              >
                {skuStatus === "checking" ? (
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <HiOutlineSearch className="text-sm" />
                )}
                Validar
              </button>
            </div>

            {skuStatus === "valid" && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
                <HiOutlineBadgeCheck className="text-emerald-500 text-base flex-shrink-0" />
                <span className="text-[11px] font-semibold text-emerald-700 truncate">
                  SKU válido{skuProductName ? ` · ${skuProductName}` : ""}
                </span>
              </div>
            )}
            {skuStatus === "invalid" && (
              <div className="flex items-center gap-2 px-3 py-2 bg-rose-50 border border-rose-100 rounded-xl">
                <HiOutlineExclamationCircle className="text-rose-500 text-base flex-shrink-0" />
                <span className="text-[11px] font-semibold text-rose-700">SKU no encontrado en el sistema</span>
              </div>
            )}
          </div>

          {/* Estado Omnix */}
          <div className="flex flex-col gap-1.5">
            <label className={LABEL_CLS}>
              Estado Omnix <span className="text-rose-500">*</span>
            </label>
            <select
              value={form.estadoOmnix}
              onChange={(e) => handleChange("estadoOmnix", e.target.value)}
              className={`${INPUT_CLS} appearance-none cursor-pointer`}
            >
              {ESTADO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Datos adicionales */}
          <div className="border-t border-gray-100 pt-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
              Datos adicionales <span className="normal-case font-medium">(opcionales)</span>
            </p>

            {/* Almacén + Validar */}
            <div className="flex flex-col gap-1.5 mb-3">
              <label className={OPT_LABEL_CLS}>Almacén</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.almacen}
                  onChange={(e) => handleChange("almacen", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && validateAlmacen()}
                  placeholder="Ej: AC8"
                  className={`${OPT_INPUT_CLS} flex-1 min-w-0`}
                />
                <button
                  type="button"
                  onClick={validateAlmacen}
                  disabled={!form.almacen.trim() || almacenStatus === "checking"}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl
                    bg-indigo-50 hover:bg-indigo-100 active:scale-[0.97]
                    text-indigo-600 text-[11px] font-black uppercase tracking-widest
                    transition-all disabled:opacity-40 disabled:bg-gray-100 disabled:text-gray-400 whitespace-nowrap"
                >
                  {almacenStatus === "checking" ? (
                    <span className="w-3.5 h-3.5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                  ) : (
                    <HiOutlineSearch className="text-sm" />
                  )}
                  Validar
                </button>
              </div>

              {almacenStatus === "valid" && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <HiOutlineBadgeCheck className="text-emerald-500 text-base flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-emerald-700 truncate">
                    Almacén válido{almacenName ? ` · ${almacenName}` : ""}
                  </span>
                </div>
              )}
              {almacenStatus === "invalid" && (
                <div className="flex items-center gap-2 px-3 py-2 bg-rose-50 border border-rose-100 rounded-xl">
                  <HiOutlineExclamationCircle className="text-rose-500 text-base flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-rose-700">Almacén no encontrado</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {([
                { field: "subalmacen", label: "Subalmacén", placeholder: "Ej: MOQUEGUA" },
                { field: "sociedad", label: "Sociedad", placeholder: "Ej: PCS" },
                { field: "centro", label: "Centro", placeholder: "Ej: TIENDA RETAIL" },
                { field: "tipoSku", label: "Tipo SKU", placeholder: "Ej: SERIADO" },
                { field: "claseSku", label: "Clase SKU", placeholder: "Ej: ZPC1" },
                { field: "almRecep", label: "Almacén Recep", placeholder: "Ej: 0102" },
              ] as const).map(({ field, label, placeholder }) => (
                <div key={field} className="flex flex-col gap-1.5">
                  <label className={OPT_LABEL_CLS}>{label}</label>
                  <input
                    type="text"
                    value={form[field]}
                    onChange={(e) => handleChange(field, e.target.value)}
                    placeholder={placeholder}
                    className={OPT_INPUT_CLS}
                  />
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-col gap-1.5">
              <label className={OPT_LABEL_CLS}>Nombre Bodega</label>
              <input
                type="text"
                value={form.nomBodega}
                onChange={(e) => handleChange("nomBodega", e.target.value)}
                placeholder="Ej: TP LIM BRIGHTSTAR NUEVOS"
                className={OPT_INPUT_CLS}
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">
              <HiOutlineExclamationCircle className="text-rose-500 text-lg flex-shrink-0 mt-0.5" />
              <p className="text-[12px] font-medium text-rose-700 leading-relaxed">{error}</p>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
              <HiOutlineCheckCircle className="text-emerald-500 text-lg flex-shrink-0" />
              <p className="text-[12px] font-medium text-emerald-700">¡Serie creada exitosamente!</p>
            </div>
          )}
        </div>

        {/* Footer — sticky */}
        <div className="flex-shrink-0 flex items-center gap-3 px-5 pt-3 pb-5 border-t border-gray-100 bg-white">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-3.5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-bold hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || success || skuStatus !== "valid" || serieStatus !== "valid" || (!!form.almacen.trim() && almacenStatus !== "valid")}
            className="flex-1 py-3.5 rounded-xl bg-indigo-600 text-white text-[13px] font-bold
              hover:bg-indigo-700 active:scale-[0.98] transition-all
              disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Guardando...
              </>
            ) : success ? (
              "¡Listo!"
            ) : (
              "Crear Serie"
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slideUp 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  )
}

export default CreateSerieModal
